import {Divider} from '@/components/ui/divider/Divider'
import {Pencil01Icon} from '@/components/ui/icon/Icon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useTranslation} from '@/translations/i18n'
import {raise} from '@/utilities/helpers'
import {useComboboxContext} from '@ariakit/react'
import {useMemo} from 'react'
import {useTheme} from 'styled-components'
import {
    StyledFillMuanuallyButton,
    StyledInputAddressAutocompleteComboboxItem,
    StyledInputAddressSelectComboboxList,
    StyledInputAddressSelectComboboxPopover
} from './style'

export const InputAddressAutocompletePopover = ({
    searchPlace,
    predictions,
    showManualForm
}: {
    searchPlace: {isLoading: boolean; isError: boolean}
    predictions: google.maps.places.AutocompletePrediction[] | null
    showManualForm: () => void
}) => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const comboboxContext = useComboboxContext() ?? raise('SelectComboboxList must be a child of SelectCombobox')
    const searchValue = comboboxContext.useState('value').trim()

    const showManualModeButton = useMemo(() => {
        // Show manual button if there are no predictions or no search value
        if (predictions == null || !searchValue) return false
        if (predictions.length == 0 && searchValue) {
            return true
        }

        const decomposedSearch = searchValue.toLowerCase().split(' ')

        // Check if all search components are found in at least one prediction
        return !predictions.some(prediction =>
            decomposedSearch.every(searchComponent => prediction.description.toLowerCase().includes(searchComponent))
        )
    }, [predictions, searchValue])

    return (
        <StyledInputAddressSelectComboboxPopover sameWidth={true}>
            <StyledInputAddressSelectComboboxList>
                {searchPlace.isLoading ? (
                    <StyledInputAddressAutocompleteComboboxItem disabled>
                        <Spinner size={16} color={palette.neutral[400]} />
                    </StyledInputAddressAutocompleteComboboxItem>
                ) : searchPlace.isError ? (
                    <StyledInputAddressAutocompleteComboboxItem disabled>
                        <InputHelpText error={t('errors:default')} />
                    </StyledInputAddressAutocompleteComboboxItem>
                ) : predictions && predictions?.length > 0 ? (
                    predictions.map(prediction => (
                        <StyledInputAddressAutocompleteComboboxItem
                            key={prediction.place_id}
                            value={prediction.place_id}
                        >
                            {prediction.description}
                        </StyledInputAddressAutocompleteComboboxItem>
                    ))
                ) : (
                    <StyledInputAddressAutocompleteComboboxItem disabled>
                        <InputHelpText helpText={t('commons:no_results')} />
                    </StyledInputAddressAutocompleteComboboxItem>
                )}
            </StyledInputAddressSelectComboboxList>
            {showManualModeButton && (
                <>
                    <Divider direction="horizontal" />
                    <StyledFillMuanuallyButton variant="ghost" shape="text" fullWidth={false} onClick={showManualForm}>
                        <Pencil01Icon />
                        {t('commons:fill_manually')}
                    </StyledFillMuanuallyButton>
                </>
            )}
        </StyledInputAddressSelectComboboxPopover>
    )
}
